import type {
    BeginSyncOptions,
    OfflineScene,
    OfflineSceneKey,
    OfflineSceneState,
    OfflineViewState,
} from "@novorender/api";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { createBimSceneFileSignedDownloadUrl } from "apis/procore/bimSceneFileSignedDownloadUrl";
import { buildMiniMapDataArgs, buildModelRevisionsArgs } from "apis/procore/modelRevisions";
import { useLazyGetMiniMapDataQuery, useLazyGetModelRevisionsQuery } from "apis/procore/procoreApi";
import { useAppSelector } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import {
    selectIncrementalCachingDisabled,
    selectIsProcoreDesign,
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
} from "slices/explorer";

import { incrementalScenePruneStore, pruneStaleMinimapFiles } from "./offlinePrune";
import { registerMinimapOfflineStore, unregisterMinimapOfflineStore } from "./offlineStore";
import {
    estimateMinimapBytes,
    minimapFileInstanceIds,
    type ResolveMinimapBaseUrl,
    runMinimapSyncPhase,
    syncMinimapFiles,
} from "./offlineSync";

export type MinimapSyncPhase = {
    /** The full sync's `syncMinimap` callback for the scene; `undefined` off mobile. */
    syncMinimap: ((scene: OfflineScene) => NonNullable<BeginSyncOptions["syncMinimap"]>) | undefined;
    /**
     * The bytes `syncMinimap` covers (see {@link estimateMinimapBytes}); `undefined` off mobile.
     * Never rejects.
     * @returns `undefined` when they can't be had.
     */
    estimateBytes: ((signal: AbortSignal) => Promise<number | undefined>) | undefined;
    /** Removes stale drawing trees from a scene just enrolled for incremental caching. Never rejects. */
    pruneEnrolled(scene: OfflineScene, state: OfflineSceneState, signal: AbortSignal): Promise<void>;
};

/**
 * The open scene's mini map tiles offline: the tiled route caches into an incremental scene and
 * reads a synced one, a full sync downloads them inside its sync, and stale drawing trees are
 * pruned from an incremental scene as it loads or is enrolled.
 */
export function useMinimapSyncPhase({
    offlineState,
    sceneKey,
    sceneDirName,
}: {
    offlineState: OfflineViewState | undefined;
    sceneKey: OfflineSceneKey;
    sceneDirName: string;
}): MinimapSyncPhase {
    const viewerSceneId = useSceneId();
    const minimapTilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const incrementalCachingDisabled = useAppSelector(selectIncrementalCachingDisabled);
    const syncMinimapFilesForScene = useSyncMinimapFiles();
    const estimateMinimapBytesForScene = useEstimateMinimapBytes();
    const currentFileInstanceIds = useMinimapCurrentFileInstanceIds();

    // On Procore web and mobile alike, as geometry's own incremental caching is. A synced scene
    // is read from even under the iOS kill switch.
    useEffect(() => {
        if (!offlineState || !minimapTilingEnabled) {
            return;
        }
        registerMinimapOfflineStore({
            getScene: () => offlineState.getScene(sceneKey),
            writeEnabled: isProcoreDesign && !incrementalCachingDisabled,
            readEnabled: true,
        });
        return unregisterMinimapOfflineStore;
    }, [offlineState, sceneKey, minimapTilingEnabled, isProcoreDesign, incrementalCachingDisabled]);

    // A previous session's trees for drawings since re-mapped or removed, pruned once per scene
    // directory as an incremental scene loads. A full sync's directory changes only inside its
    // syncs. A scene not armed yet is pruned as it is enrolled.
    const prunedSceneDirName = useRef<string>();
    useEffect(() => {
        if (!offlineState || !minimapTilingEnabled || !isProcoreDesign || prunedSceneDirName.current === sceneDirName) {
            return;
        }
        const scene = offlineState.getScene(sceneKey);
        if (!scene) {
            return;
        }

        const abortController = new AbortController();
        (async () => {
            const state = await scene.getState();
            if (state.kind !== "incremental") {
                prunedSceneDirName.current = sceneDirName;
                return;
            }
            const current = await currentFileInstanceIds(viewerSceneId);
            await pruneStaleMinimapFiles({
                store: incrementalScenePruneStore(scene),
                currentFileInstanceIds: current,
                abortSignal: abortController.signal,
            });
            // An abort throws before this, and an unknown drawing set must leave a later render
            // to retry: either latched, this scene would get no prune.
            if (current !== undefined) {
                prunedSceneDirName.current = sceneDirName;
            }
        })().catch(() => {
            // Best-effort: the next scene load prunes again.
        });

        return () => abortController.abort();
    }, [
        offlineState,
        sceneKey,
        sceneDirName,
        minimapTilingEnabled,
        isProcoreDesign,
        viewerSceneId,
        currentFileInstanceIds,
    ]);

    // Mobile only, permanently, as the Settings download button is; unlike the incremental
    // caching above.
    const attemptMinimapSync = isMobileDesign && minimapTilingEnabled;
    return useMemo(
        () => ({
            syncMinimap: attemptMinimapSync
                ? (scene) => (files, signal) =>
                      runMinimapSyncPhase({
                          files,
                          // The folder the SDK hands over has no read; a read changes nothing.
                          read: (name) => scene.dir.read(name),
                          download: (folder, signal) => syncMinimapFilesForScene(viewerSceneId, folder, signal),
                          currentFileInstanceIds: () => currentFileInstanceIds(viewerSceneId),
                          signal,
                      })
                : undefined,
            estimateBytes: attemptMinimapSync
                ? (signal) => estimateMinimapBytesForScene(viewerSceneId, signal)
                : undefined,
            async pruneEnrolled(scene, state, signal) {
                if (!minimapTilingEnabled || (state.kind !== "incremental" && state.kind !== "absent")) {
                    return;
                }
                try {
                    await pruneStaleMinimapFiles({
                        store: incrementalScenePruneStore(scene),
                        currentFileInstanceIds: await currentFileInstanceIds(viewerSceneId),
                        abortSignal: signal,
                    });
                } catch {
                    // Best-effort: the next scene load prunes again.
                }
            },
        }),
        [
            attemptMinimapSync,
            minimapTilingEnabled,
            viewerSceneId,
            syncMinimapFilesForScene,
            estimateMinimapBytesForScene,
            currentFileInstanceIds,
        ],
    );
}

/**
 * The open model's drawings' `platformFileInstanceId`s, from the same lazy queries the mini map
 * UI uses, with the same arguments (`buildMiniMapDataArgs`): the revision open, and the open
 * Snapshot's slice. Always refetched: a full sync persists the current tile sets, and a prune must
 * not take a re-mapped drawing for a removed one.
 * @returns `undefined` without a Procore context or a revision of the scene: the drawings couldn't be
 *   asked for. `fileInstanceIds` is `undefined` for a payload that can't say which tile sets are
 *   current (see {@link minimapFileInstanceIds}): nothing is downloaded or pruned for it.
 * @throws When a query fails.
 */
function useFetchMinimapFileInstanceIds() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getMiniMapData] = useLazyGetMiniMapDataQuery();

    return useCallback(
        async (sceneId: string): Promise<{ fileInstanceIds: string[] | undefined } | undefined> => {
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }
            const miniMapDataArgs = buildMiniMapDataArgs(
                procoreContext,
                await getModelRevisions(revisionsArgs).unwrap(),
                sceneId,
            );
            if (!miniMapDataArgs) {
                return undefined;
            }
            const payload = await getMiniMapData(miniMapDataArgs).unwrap();
            return { fileInstanceIds: minimapFileInstanceIds(payload, procoreContext.sceneSliceId) };
        },
        [procoreContext, getModelRevisions, getMiniMapData],
    );
}

/**
 * Downloads every drawing's tile set into the sync's mini map folder.
 * @returns The drawings it downloaded for, with what it left out, so the prune that follows needn't
 *   ask again; `undefined` when it couldn't ask which drawings the model has. Nothing downloaded,
 *   and nothing left out, for an answer that can't say which tile sets are current.
 */
function useSyncMinimapFiles() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const fetchFileInstanceIds = useFetchMinimapFileInstanceIds();

    return useCallback(
        async (sceneId: string, folder: Parameters<typeof syncMinimapFiles>[0]["folder"], abortSignal: AbortSignal) => {
            const drawings = await fetchFileInstanceIds(sceneId);
            if (!procoreContext || !drawings) {
                return undefined;
            }
            const { fileInstanceIds } = drawings;
            if (!fileInstanceIds) {
                return { fileInstanceIds, report: { skippedDrawings: 0, skippedFiles: 0 } };
            }
            const report = await syncMinimapFiles({
                folder,
                fileInstanceIds,
                resolveBaseUrl: minimapBaseUrlResolver(procoreContext, sceneId),
                abortSignal,
            });
            return { fileInstanceIds, report };
        },
        [procoreContext, fetchFileInstanceIds],
    );
}

/** What {@link useSyncMinimapFiles} would download, by {@link estimateMinimapBytes}. Never rejects. */
function useEstimateMinimapBytes() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const fetchFileInstanceIds = useFetchMinimapFileInstanceIds();

    return useCallback(
        async (sceneId: string, abortSignal: AbortSignal): Promise<number | undefined> => {
            try {
                const drawings = await fetchFileInstanceIds(sceneId);
                if (!procoreContext || !drawings) {
                    return undefined;
                }
                if (!drawings.fileInstanceIds) {
                    return 0;
                }
                return await estimateMinimapBytes({
                    fileInstanceIds: drawings.fileInstanceIds,
                    resolveBaseUrl: minimapBaseUrlResolver(procoreContext, sceneId),
                    abortSignal,
                });
            } catch {
                return undefined;
            }
        },
        [procoreContext, fetchFileInstanceIds],
    );
}

function minimapBaseUrlResolver(
    procoreContext: NonNullable<ReturnType<typeof selectProcoreContext>>,
    sceneId: string,
): ResolveMinimapBaseUrl {
    return (fileInstanceId, options) =>
        createBimSceneFileSignedDownloadUrl(
            {
                companyId: procoreContext.companyId,
                projectId: procoreContext.projectId,
                sceneId,
                sceneVersionId: procoreContext.sceneVersionId ?? "current",
                fileInstanceId,
            },
            options,
        );
}

/**
 * The drawings a prune keeps, or `undefined` when they can't be had (offline, or a query failed):
 * a prune takes "don't know" as "prune nothing", never as "every drawing is gone".
 */
function useMinimapCurrentFileInstanceIds() {
    const fetchFileInstanceIds = useFetchMinimapFileInstanceIds();

    return useCallback(
        async (sceneId: string): Promise<string[] | undefined> => {
            if (!navigator.onLine) {
                return undefined;
            }
            return fetchFileInstanceIds(sceneId).then(
                (drawings) => drawings?.fileInstanceIds,
                () => undefined,
            );
        },
        [fetchFileInstanceIds],
    );
}
